import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';

export interface GlobalCommerceCurrencyRow {
  id: string;
  code: string;
  name: string;
  symbol: string;
  decimalPlaces: number;
  isActive: boolean;
}

export interface GlobalCommerceFxRateRow {
  id: string;
  effectiveDateUtc: string;
  fromCurrencyCode: string;
  toCurrencyCode: string;
  rate: number;
  approvalStatus: string;
}

export interface GlobalCommerceTaxJurisdictionRow {
  id: string;
  jurisdictionCode: string;
  countryCode: string;
  regionCode?: string | null;
  currencyCode: string;
  isActive: boolean;
}

export interface GlobalCommerceTaxCategoryRow {
  id: string;
  categoryCode: string;
  name: string;
  isExempt: boolean;
  isZeroRated: boolean;
  isActive: boolean;
}

export interface GlobalCommerceTaxRateRow {
  id: string;
  taxJurisdictionId: string;
  taxCategoryId: string;
  ratePercent: number;
  effectiveFromUtc: string;
  effectiveToUtc?: string | null;
  isActive: boolean;
}

export interface GlobalCommerceTaxRuleRow {
  id: string;
  taxJurisdictionId: string;
  taxCategoryId: string;
  pricingMode: string;
  isExempt: boolean;
  isZeroRated: boolean;
  isActive: boolean;
}

export interface GlobalCommerceJurisdictionPackRow {
  id: string;
  jurisdictionCode: string;
  countryCode: string;
  regionCode?: string | null;
  isEnabled: boolean;
  allowCrossBorder: boolean;
  allowedCurrenciesJson?: string | null;
  allowedPaymentMethodsJson?: string | null;
  restrictedCategoryCodesJson?: string | null;
  requiredInvoiceFieldsJson?: string | null;
}

export interface GlobalCommerceShippingZoneRow {
  id: string;
  zoneCode: string;
  name: string;
  countryCode: string;
  regionCode?: string | null;
  isActive: boolean;
}

export interface GlobalCommerceShippingRuleRow {
  id: string;
  shippingZoneId: string;
  shippingMethodCode: string;
  minSubtotal: number;
  maxSubtotal?: number | null;
  isActive: boolean;
}

export interface GlobalCommerceShippingRateRow {
  id: string;
  shippingRuleId: string;
  currencyCode: string;
  flatAmount: number;
  isActive: boolean;
}

export interface GlobalCommerceReadinessSummary {
  launchDecision: string;
  readyCount: number;
  conditionalCount: number;
  blockerCount: number;
  checks: Array<{ key: string; label: string; count: number; status: string; notes: string }>;
  deferrals: string[];
}

export interface EInvoiceRequirementResult {
  jurisdictionCode: string;
  isRequired: boolean;
  providerKey: string;
  formatCode: string;
  requiredFields: string[];
}

@Injectable({ providedIn: 'root' })
export class GlobalCommerceApiService {
  private readonly http = inject(HttpClient);
  private readonly base = `${environment.apiBaseUrl}/api/v1/marketplace/global-commerce`;

  listCurrencies(): Observable<GlobalCommerceCurrencyRow[]> {
    return this.http.get<GlobalCommerceCurrencyRow[]>(`${this.base}/currencies`);
  }

  listFxRates(filters?: { from?: string; to?: string; effectiveDateUtc?: string }): Observable<GlobalCommerceFxRateRow[]> {
    let params = new HttpParams();
    if (filters?.from) params = params.set('fromCurrencyCode', filters.from);
    if (filters?.to) params = params.set('toCurrencyCode', filters.to);
    if (filters?.effectiveDateUtc) params = params.set('effectiveDateUtc', filters.effectiveDateUtc);
    return this.http.get<GlobalCommerceFxRateRow[]>(`${this.base}/fx-rates`, { params });
  }

  upsertFxRate(payload: { effectiveDateUtc: string; fromCurrencyCode: string; toCurrencyCode: string; rate: number }) {
    return this.http.post(`${this.base}/fx-rates`, payload);
  }

  listTaxJurisdictions(): Observable<GlobalCommerceTaxJurisdictionRow[]> {
    return this.http.get<GlobalCommerceTaxJurisdictionRow[]>(`${this.base}/tax-jurisdictions`);
  }

  upsertTaxJurisdiction(payload: {
    jurisdictionCode: string;
    countryCode: string;
    regionCode?: string | null;
    currencyCode: string;
    isActive: boolean;
  }) {
    return this.http.post(`${this.base}/tax-jurisdictions`, payload);
  }

  listTaxCategories(): Observable<GlobalCommerceTaxCategoryRow[]> {
    return this.http.get<GlobalCommerceTaxCategoryRow[]>(`${this.base}/tax-categories`);
  }

  upsertTaxCategory(payload: {
    categoryCode: string;
    name: string;
    isExempt: boolean;
    isZeroRated: boolean;
    isActive: boolean;
  }) {
    return this.http.post(`${this.base}/tax-categories`, payload);
  }

  listTaxRates(): Observable<GlobalCommerceTaxRateRow[]> {
    return this.http.get<GlobalCommerceTaxRateRow[]>(`${this.base}/tax-rates`);
  }

  upsertTaxRate(payload: {
    taxJurisdictionId: string;
    taxCategoryId: string;
    ratePercent: number;
    effectiveFromUtc: string;
    effectiveToUtc?: string | null;
    isActive: boolean;
  }) {
    return this.http.post(`${this.base}/tax-rates`, payload);
  }

  listTaxRules(): Observable<GlobalCommerceTaxRuleRow[]> {
    return this.http.get<GlobalCommerceTaxRuleRow[]>(`${this.base}/tax-rules`);
  }

  upsertTaxRule(payload: {
    taxJurisdictionId: string;
    taxCategoryId: string;
    pricingMode: string;
    isExempt: boolean;
    isZeroRated: boolean;
    isActive: boolean;
  }) {
    return this.http.post(`${this.base}/tax-rules`, payload);
  }

  listJurisdictionPacks(): Observable<GlobalCommerceJurisdictionPackRow[]> {
    return this.http.get<GlobalCommerceJurisdictionPackRow[]>(`${this.base}/jurisdiction-packs`);
  }

  upsertJurisdictionPack(payload: {
    jurisdictionCode: string;
    countryCode: string;
    regionCode?: string | null;
    isEnabled: boolean;
    allowCrossBorder: boolean;
    allowedCurrenciesJson?: string | null;
    allowedPaymentMethodsJson?: string | null;
    restrictedCategoryCodesJson?: string | null;
    requiredInvoiceFieldsJson?: string | null;
  }) {
    return this.http.post(`${this.base}/jurisdiction-packs`, payload);
  }

  listShippingZones(): Observable<GlobalCommerceShippingZoneRow[]> {
    return this.http.get<GlobalCommerceShippingZoneRow[]>(`${this.base}/shipping-zones`);
  }

  upsertShippingZone(payload: {
    zoneCode: string;
    name: string;
    countryCode: string;
    regionCode?: string | null;
    isActive: boolean;
  }) {
    return this.http.post(`${this.base}/shipping-zones`, payload);
  }

  listShippingRules(): Observable<GlobalCommerceShippingRuleRow[]> {
    return this.http.get<GlobalCommerceShippingRuleRow[]>(`${this.base}/shipping-rules`);
  }

  upsertShippingRule(payload: {
    shippingZoneId: string;
    shippingMethodCode: string;
    minSubtotal: number;
    maxSubtotal?: number | null;
    isActive: boolean;
  }) {
    return this.http.post(`${this.base}/shipping-rules`, payload);
  }

  listShippingRates(): Observable<GlobalCommerceShippingRateRow[]> {
    return this.http.get<GlobalCommerceShippingRateRow[]>(`${this.base}/shipping-rates`);
  }

  upsertShippingRate(payload: {
    shippingRuleId: string;
    currencyCode: string;
    flatAmount: number;
    isActive: boolean;
  }) {
    return this.http.post(`${this.base}/shipping-rates`, payload);
  }

  checkEInvoiceRequirements(jurisdictionCode: string): Observable<EInvoiceRequirementResult> {
    return this.http.post<EInvoiceRequirementResult>(`${this.base}/einvoice-requirements/check`, { jurisdictionCode });
  }

  getReadinessSummary(): Observable<GlobalCommerceReadinessSummary> {
    return this.http.get<GlobalCommerceReadinessSummary>(`${this.base}/readiness`);
  }
}
